import { useMemo, type FC } from "react"
import { useTranslation } from "react-i18next"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"

import type { RecordType, SingleStatPlotView } from "types/dashboard.types"

import useSingleStatConfig from "./configuration/useSingleStatConfig"

interface SingleStatProps {
  view: SingleStatPlotView
  data: RecordType[]
}

const SingleStat: FC<SingleStatProps> = ({ view, data }) => {
  const { i18n, t } = useTranslation()
  const locale = i18n.resolvedLanguage
  const config = useSingleStatConfig({ view, data, locale, t })

  const { fontSize, footerBottomDistance } = useMemo(() => {
    switch (config.size) {
      case "md":
        return {
          fontSize: "1.5rem",
          footerBottomDistance: "1rem",
        }
      case "lg":
        return {
          fontSize: "1.8rem",
          footerBottomDistance: "1.5rem",
        }
      case "sm":
      default:
        return {
          fontSize: "1.1rem",
          footerBottomDistance: undefined,
        }
    }
  }, [config])

  return (
    <>
      <Stack alignItems="center" height={"100%"}>
        <Stack alignItems="center" justifyContent="center" height={"80%"}>
          {config && !config.isValid && (
            <Typography component="p" color="error" data-testid="stat-error">
              {config.error?.message &&
                t([`error.${config.error.message}`, config.error.message])}
            </Typography>
          )}
          {config?.isValid && (
            <>
              <Typography
                sx={{
                  fontSize,
                  fontWeight: "700",
                  textAlign: "center",
                }}
                color={config.textColor}
                data-testid="stat-value"
              >
                {config.data}
              </Typography>
            </>
          )}
        </Stack>
        {config?.isValid &&
          config.footer.length > 0 &&
          (config.size === "md" || config.size === "lg") && (
            <Stack
              position="absolute"
              bottom={footerBottomDistance}
              alignItems="center"
              textAlign="center"
              data-testid="stat-footer"
              maxWidth="90%"
            >
              {config.footer.map((line, index) => (
                <Typography key={index} variant="body2" color="text.secondary">
                  {line}
                </Typography>
              ))}
            </Stack>
          )}
      </Stack>
    </>
  )
}

export default SingleStat
